import { useState, useCallback, useEffect, useRef } from "react";
import {
  getUsersService,
  updateUserService,
  deleteUserService,
  getBikesService,
  updateBikeService,
  deleteBikeService,
} from "modules/came/services/bicicletasService";

const useUsers = (refreshFlag) => {
  const [users, setUsers] = useState([]);
  const [bikes, setBikes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadingBikes, setLoadingBikes] = useState(false);
  const [error, setError] = useState(null);

  const isMounted = useRef(true);
  const hasLoadedRef = useRef(false);

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  // ========== CARGAR USUARIOS ==========
  const loadUsers = useCallback(async () => {
    setLoading(true);
    setError(null);

    try {
      const result = await getUsersService();
      if (!isMounted.current) return;

      if (result.success) {
        setUsers(Array.isArray(result.data) ? result.data : []);
      } else {
        setError(result.error);
      }
    } catch (err) {
      if (isMounted.current) setError(err.message || "Error al cargar usuarios");
    } finally {
      if (isMounted.current) setLoading(false);
    }
  }, []);

  // ========== CARGAR BICICLETAS ==========
  const loadBikes = useCallback(async () => {
    setLoadingBikes(true);
    try {
      const result = await getBikesService();
      if (!isMounted.current) return [];

      if (result?.success) {
        const list = Array.isArray(result.data) ? result.data : [];
        setBikes(list);
        return list;
      }
      return [];
    } catch (err) {
      console.error("Error al cargar bicicletas:", err);
      return [];
    } finally {
      if (isMounted.current) setLoadingBikes(false);
    }
  }, []);

  // ========== ACTUALIZAR USUARIO ==========
  const updateUser = useCallback(
    async (payload) => {
      const result = await updateUserService(payload);
      if (result.success) {
        await loadUsers();
      }
      return result;
    },
    [loadUsers]
  );

  // ========== ELIMINAR USUARIO ==========
  const deleteUser = useCallback(
    async (id) => {
      const result = await deleteUserService(id);
      if (result.success) {
        await loadUsers();
      }
      return result;
    },
    [loadUsers]
  );

  // ========== ACTUALIZAR BICICLETA ==========
  const updateBike = useCallback(
    async (payload) => {
      const result = await updateBikeService(payload);
      if (result.success) {
        await loadBikes();
      }
      return result;
    },
    [loadBikes]
  );

  // ========== ELIMINAR BICICLETA ==========
  const deleteBike = useCallback(
    async (id) => {
      const result = await deleteBikeService(id);
      if (result.success) {
        await loadBikes();
      }
      return result;
    },
    [loadBikes]
  );

  // ========== OBTENER BICIS POR USUARIO ==========
  const getBikesByUserId = useCallback(
    (userId) => {
      if (!Array.isArray(bikes) || !userId) return [];
      return bikes.filter((b) => String(b.id_user) === String(userId));
    },
    [bikes]
  );

  // ========== CARGA INICIAL ==========
  useEffect(() => {
    if (!hasLoadedRef.current && isMounted.current) {
      hasLoadedRef.current = true;
      loadUsers();
      loadBikes();
    }
  }, [loadUsers, loadBikes]);

  // ========== REFRESH ==========
  useEffect(() => {
    if (refreshFlag && hasLoadedRef.current) {
      loadUsers();
      loadBikes();
    }
  }, [refreshFlag, loadUsers, loadBikes]);

  return {
    users,
    bikes,
    loading,
    loadingBikes,
    error,
    loadUsers,
    loadBikes,
    updateUser,
    deleteUser,
    updateBike,
    deleteBike,
    getBikesByUserId,
    totalUsers: users.length,
    activeUsers: users.filter((u) => Number(u.status) === 1).length,
    inactiveUsers: users.filter((u) => Number(u.status) !== 1).length,
  };
};

export default useUsers;
